import React, { useEffect, useRef, useState } from 'react'
import {
  APIProvider,
  InfoWindow as VisInfoWindow,
  Map,
  Marker as VisMarker,
  Polyline as VisPolyline,
  useMap,
} from '@vis.gl/react-google-maps'
import { MarkerClusterer as GoogleMarkerClusterer } from '@googlemaps/markerclusterer'
import {
  GOOGLE_MAPS_LIBRARIES,
} from './googleMaps'

function apiKey() {
  return import.meta.env.VITE_GOOGLE_APIKEY || ''
}

function toLatLngLiteral(value) {
  if (!value) return value
  if (typeof value.lat === 'function') {
    return { lat: value.lat(), lng: value.lng() }
  }
  return { lat: Number(value.lat), lng: Number(value.lng) }
}

function toLegacyMouseEvent(event) {
  const raw = event?.latLng || event?.detail?.latLng
  if (!raw) return event
  const { lat, lng } = toLatLngLiteral(raw)
  return {
    ...event,
    latLng: {
      lat: () => lat,
      lng: () => lng,
    },
  }
}

function MapLoadBridge({ onLoad  }) {
  const map = useMap()
  useEffect(() => {
    if (map) onLoad?.(map)
  }, [map, onLoad])
  return null
}

export function GoogleMap({
  mapContainerStyle,
  zoom,
  center,
  onLoad,
  onClick,
  onMouseOut,
  onMouseMove,
  onIdle,
  options = {},
  children,
  ...rest
}) {
  return (
    <APIProvider apiKey={apiKey()} libraries={GOOGLE_MAPS_LIBRARIES}>
      <Map
        style={mapContainerStyle}
        defaultCenter={toLatLngLiteral(center)}
        defaultZoom={zoom}
        gestureHandling={options.gestureHandling}
        fullscreenControl={options.fullscreenControl}
        mapTypeControl={options.mapTypeControl}
        streetViewControl={options.streetViewControl}
        colorScheme={options.colorScheme}
        styles={options.styles}
        onClick={(event) => onClick?.(toLegacyMouseEvent(event))}
        onMousemove={(event) => onMouseMove?.(toLegacyMouseEvent(event))}
        onMouseout={() => onMouseOut?.()}
        onIdle={() => onIdle?.()}
        {...rest}
      >
        {onLoad ? <MapLoadBridge onLoad={onLoad} /> : null}
        {children}
      </Map>
    </APIProvider>
  )
}

export function Marker({
  position,
  clusterer,
  icon,
  label,
  zIndex,
  onClick,
  onMouseOver,
  children,
}) {
  const map = useMap()
  const markerRef = useRef(null)

  useEffect(() => {
    if (!clusterer || !window.google?.maps) return undefined
    const marker = new window.google.maps.Marker({
      position: toLatLngLiteral(position),
      icon,
      label,
      zIndex,
      map: null,
    })
    markerRef.current = marker
    if (onClick) marker.addListener('click', onClick)
    if (onMouseOver) marker.addListener('mouseover', onMouseOver)
    clusterer.addMarker(marker)
    return () => {
      clusterer.removeMarker(marker)
      marker.setMap(null)
      markerRef.current = null
    }
  }, [clusterer, icon, label, onClick, onMouseOver, position, zIndex])

  if (clusterer) {
    return <>{children}</>
  }

  if (!map) return children || null

  return (
    <>
      <VisMarker
        position={toLatLngLiteral(position)}
        icon={icon}
        label={label}
        zIndex={zIndex}
        onClick={onClick}
        onMouseOver={onMouseOver}
      />
      {children}
    </>
  )
}

function clusterFillColor(count) {
  if (count >= 30) return { fill: '#f4772e', text: '#fff' }
  if (count >= 10) return { fill: '#ffc926', text: '#3b3e66' }
  return { fill: '#4191ff', text: '#fff' }
}

const housefoliosClusterRenderer = {
  render({ count, position  }) {
    const { fill, text } = clusterFillColor(count)
    const svg = window.btoa(
      `<svg fill="${fill}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240"><circle cx="120" cy="120" opacity=".55" r="70" /><circle cx="120" cy="120" r="52" /></svg>`,
    )
    return new window.google.maps.Marker({
      position,
      icon: {
        url: `data:image/svg+xml;base64,${svg}`,
        scaledSize: new window.google.maps.Size(48, 48),
        anchor: new window.google.maps.Point(24, 24),
      },
      label: {
        text: String(count),
        color: text,
        fontSize: '13px',
        fontWeight: '700',
      },
      zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
      title: `${count} properties`,
    })
  },
}

export function MarkerClusterer({ options = {}, children }) {
  const map = useMap()
  const [clusterer, setClusterer] = useState(null)

  useEffect(() => {
    if (!map) return undefined
    const next = new GoogleMarkerClusterer({
      map,
      markers: [],
      algorithmOptions: {
        maxZoom: options.maxZoom,
        radius: options.gridSize,
      },
      renderer: housefoliosClusterRenderer,
    })
    setClusterer(next)
    return () => {
      next.clearMarkers()
      setClusterer(null)
    }
  }, [map, options.gridSize, options.maxZoom])

  if (!clusterer) return null
  return children(clusterer)
}

export function Polyline({ path, options = {}, onClick }) {
  return (
    <VisPolyline
      path={path}
      onClick={onClick}
      strokeColor={options.strokeColor}
      editable={options.editable}
      draggable={options.draggable}
      geodesic={options.geodesic}
    />
  )
}

export function InfoWindow({
  position,
  onCloseClick,
  options,
  children,
}) {
  const pixelOffset = options?.pixelOffset
  const offset = pixelOffset
    ? [
        typeof pixelOffset.width === 'number'
          ? pixelOffset.width
          : pixelOffset.width?.(),
        typeof pixelOffset.height === 'number'
          ? pixelOffset.height
          : pixelOffset.height?.(),
      ]
    : undefined

  return (
    <VisInfoWindow
      position={toLatLngLiteral(position)}
      onCloseClick={onCloseClick}
      pixelOffset={offset}
      headerDisabled
    >
      {children}
    </VisInfoWindow>
  )
}

export function StreetViewPanorama({ options = {}, onCloseClick }) {
  const map = useMap()

  useEffect(() => {
    if (!map) return undefined
    const panorama = map.getStreetView()
    panorama.setOptions({
      position: options.position,
      visible: Boolean(options.visible),
      pov: options.pov,
    })
    const listener = onCloseClick
      ? panorama.addListener('closeclick', onCloseClick)
      : null
    return () => {
      listener?.remove()
      if (!options.visible) panorama.setVisible(false)
    }
  }, [map, onCloseClick, options.position, options.pov, options.visible])

  return null
}
